/**
 * Chauffeur Mission Detail — Refonte Mobile EPIC 3 (Ticket 5/6 preview)
 * Vertical layout, responsive status buttons, accordion-ready sections.
 * Content-only: header from layout.
 */
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useActionGuard } from '../../hooks/useActionGuard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, MapPin, Calendar, Clock, User, Car, Phone,
  Mail, CheckCircle2, Navigation, Flag, AlertTriangle,
  Loader2, History, ChevronDown, ChevronUp, PauseCircle, PlayCircle,
  Wifi, WifiOff, LocateFixed
} from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

/**
 * Robust GPS position getter with retry + fallback.
 * 1st try: high accuracy, 15s timeout, accept cached pos up to 30s old
 * 2nd try: low accuracy, 15s timeout, accept cached pos up to 60s old
 */
const getGpsPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error('Géolocalisation non supportée'));
  navigator.geolocation.getCurrentPosition(
    pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
    () => {
      // Fallback: retry without high accuracy
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        err => reject(err),
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
      );
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
  );
});

const STATUS_BADGE = {
  assigned: { label: 'Assignée', cls: 'bg-blue-500/20 text-blue-400' },
  accepted: { label: 'Acceptée', cls: 'bg-purple-500/20 text-purple-400' },
  confirmed: { label: 'Confirmée', cls: 'bg-green-500/20 text-green-400' },
  en_route: { label: 'En route', cls: 'bg-yellow-500/20 text-yellow-400' },
  on_site: { label: 'Sur place', cls: 'bg-orange-500/20 text-orange-400' },
  waiting_for_pickup: { label: 'En attente client', cls: 'bg-orange-500/20 text-orange-400' },
  client_on_board: { label: 'Client à bord', cls: 'bg-cyan-500/20 text-cyan-400' },
  dropped_off: { label: 'Client déposé', cls: 'bg-indigo-500/20 text-indigo-400' },
  arret: { label: 'Arrêt', cls: 'bg-amber-500/20 text-amber-400' },
  resume_after_stop: { label: 'Reprise', cls: 'bg-emerald-500/20 text-emerald-400' },
  completed: { label: 'Terminée', cls: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Annulée', cls: 'bg-red-500/20 text-red-400' },
};

const NEXT_BTN = {
  accepted: { label: 'Accepter la mission', icon: CheckCircle2, color: 'bg-green-600 hover:bg-green-700' },
  en_route: { label: 'En approche', icon: Navigation, color: 'bg-blue-600 hover:bg-blue-700' },
  waiting_for_pickup: { label: 'Arrivé sur place', icon: MapPin, color: 'bg-orange-600 hover:bg-orange-700' },
  client_on_board: { label: 'Client à bord', icon: User, color: 'bg-cyan-600 hover:bg-cyan-700' },
  dropped_off: { label: 'Client déposé', icon: MapPin, color: 'bg-indigo-600 hover:bg-indigo-700' },
  completed: { label: 'Terminer la mission', icon: Flag, color: 'bg-green-600 hover:bg-green-700' },
};

const vehicleNames = { berline: 'Berline', beline_vip: 'Berline Premium', suv: 'SUV Premium', van: 'Van' };
const optionNames = { child_seat: 'Siège bébé', welcome_sign: "Panneau d'accueil", water_bottle: "Bouteille d'eau" };
const incidentTypes = [
  { value: 'delay', label: 'Retard' },
  { value: 'vehicle_issue', label: 'Problème véhicule' },
  { value: 'client_issue', label: 'Problème client' },
  { value: 'route_issue', label: 'Problème trajet' },
  { value: 'other', label: 'Autre' },
];

const fmtFull = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
const fmtShort = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/* Collapsible section */
function Section({ title, icon: Icon, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-[#161616] border-white/6 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left min-h-[48px] touch-manipulation"
        data-testid={testId}
      >
        <span className="text-sm font-semibold text-white flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#C9A24D]" /> {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>
      {open && <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>}
    </Card>
  );
}

const ChauffeurMissionDetailPage = () => {
  const { id: missionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTVA = user?.tva_assujetti === true;
  const TVA_RATE = 0.10;

  const formatFee = (fee) => {
    if (!fee) return null;
    const ht = parseFloat(fee);
    const amount = isTVA ? (ht * (1 + TVA_RATE)).toFixed(2) : ht.toFixed(2);
    const label = isTVA ? 'TTC' : 'HT';
    return { amount, label };
  };

  const backTo = '/chauffeur/missions?period=upcoming';
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const statusAction = useActionGuard();
  const incidentAction = useActionGuard();
  const stopAction = useActionGuard();
  const [showIncident, setShowIncident] = useState(false);
  const [incidentType, setIncidentType] = useState('');
  const [incidentDesc, setIncidentDesc] = useState('');
  const [incidentSev, setIncidentSev] = useState('medium');
  const [showStopModal, setShowStopModal] = useState(false);
  const [stopReason, setStopReason] = useState('');
  const [gpsError, setGpsError] = useState(null);
  const resumeAction = useActionGuard();

  // Guard: access online status + GPS from layout context
  let isOnline = true, gps = null;
  try {
    const ctx = useOutletContext() || {};
    isOnline = ctx.isOnline ?? true;
    gps = ctx.gps ?? null;
  } catch { /* not inside Outlet — fallback safe */ }

  const gpsActive = gps ? gps.gpsStatus === 'tracking' : true;
  const canStartMission = isOnline && gpsActive;

  useEffect(() => {
    fetchMission();
  }, [missionId]);

  const fetchMission = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await axios.get(`${API_URL}/api/chauffeur/missions/${missionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMission(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        alert("Vous n'êtes pas autorisé à voir cette mission");
        navigate('/chauffeur');
      }
    } finally {
      setLoading(false);
    }
  };

  // T7: Resume after stop
  const handleResumeAfterStop = resumeAction.guard(async () => {
    const token = localStorage.getItem('access_token');
    let lat = null, lng = null;
    try {
      const pos = await getGpsPosition();
      lat = pos.lat;
      lng = pos.lng;
    } catch (e) { /* GPS optional */ }

    await axios.post(
      `${API_URL}/api/chauffeur/missions/${missionId}/resume-after-stop`,
      { lat, lng },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(err => { alert(err.response?.data?.detail || 'Erreur'); throw err; });
    fetchMission();
  });

  const handleUpdateStatus = statusAction.guard(async () => {
    if (!mission?.next_status) return;
    setGpsError(null);
    const token = localStorage.getItem('access_token');
    
    // Get current GPS position for validation
    let lat = null, lng = null;
    try {
      const pos = await getGpsPosition();
      lat = pos.lat;
      lng = pos.lng;
    } catch (e) {
      // GPS not available - only block for location-sensitive statuses
      if (['on_site', 'completed'].includes(mission.next_status)) {
        setGpsError("Impossible d'obtenir votre position GPS. Vérifiez que la géolocalisation est bien activée et réessayez.");
        return;
      }
    }

    try {
      await axios.post(
        `${API_URL}/api/chauffeur/missions/${missionId}/update-status`,
        { new_status: mission.next_status, lat, lng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMission();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('position')) {
        setGpsError(detail);
      } else {
        alert(detail || 'Erreur');
      }
      throw err;
    }
  });

  const handleReportStop = stopAction.guard(async () => {
    if (!stopReason.trim()) { alert('Veuillez indiquer la raison de l\'arrêt'); return; }
    const token = localStorage.getItem('access_token');
    
    let lat = null, lng = null;
    try {
      const pos = await getGpsPosition();
      lat = pos.lat;
      lng = pos.lng;
    } catch (e) { /* GPS optional for stops */ }

    await axios.post(
      `${API_URL}/api/chauffeur/missions/${missionId}/report-stop`,
      { reason: stopReason.trim(), lat, lng },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(err => { alert(err.response?.data?.detail || 'Erreur'); throw err; });
    
    setShowStopModal(false);
    setStopReason('');
    fetchMission();
  });

  const handleReportIncident = incidentAction.guard(async () => {
    if (!incidentType || !incidentDesc) { alert('Veuillez remplir tous les champs'); return; }
    const token = localStorage.getItem('access_token');
    await axios.post(
      `${API_URL}/api/chauffeur/missions/${missionId}/incident`,
      { incident_type: incidentType, description: incidentDesc, severity: incidentSev },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(err => { alert(err.response?.data?.detail || 'Erreur'); throw err; });
    setShowIncident(false);
    setIncidentType('');
    setIncidentDesc('');
    alert('Incident signalé avec succès');
    fetchMission();
  });

  const badge = (status) => {
    const c = STATUS_BADGE[status] || STATUS_BADGE.assigned;
    return <span className={`px-2 py-0.5 rounded text-xs ${c.cls}`}>{c.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-7 h-7 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[#E5E5E5]/50">Mission non trouvée</p>
      </div>
    );
  }

  const isInProgress = ['en_route', 'on_site', 'client_on_board'].includes(mission.status);
  const showContact = ['en_route', 'on_site', 'client_on_board', 'completed'].includes(mission.status);
  const nextCfg = mission.next_status ? NEXT_BTN[mission.next_status] : null;
  const displayStatus = mission.is_stopped ? 'arret' : mission.status;

  return (
    <div className="px-4 py-4 max-w-screen-lg mx-auto space-y-3" data-testid="chauffeur-mission-detail">
      {/* Back + Ref */}
      <div className="flex items-center gap-3 mb-1">
        <Link
          to={backTo}
          className="p-2 -ml-2 rounded-lg text-[#E5E5E5]/50 hover:text-[#C9A24D] min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-base font-bold text-[#C9A24D] font-mono">{mission.booking_ref}</h1>
            {badge(displayStatus)}
          </div>
          <p className="text-[#E5E5E5]/35 text-xs">{fmtFull(mission.pickup_datetime)}</p>
        </div>
      </div>

      {/* Guard: GPS + Online required for mission start */}
      {nextCfg && mission.next_status === 'en_route' && !canStartMission && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl" data-testid="guard-gps-online">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-amber-300 text-sm font-medium">Impossible de démarrer</p>
            {!isOnline && (
              <p className="text-amber-400/70 text-xs flex items-center gap-1.5">
                <WifiOff className="w-3.5 h-3.5" /> Activez le mode <b>En ligne</b> depuis le menu
              </p>
            )}
            {!gpsActive && (
              <p className="text-amber-400/70 text-xs flex items-center gap-1.5">
                <LocateFixed className="w-3.5 h-3.5" /> Activez la <b>géolocalisation</b> sur votre appareil
              </p>
            )}
          </div>
        </div>
      )}

      {/* GPS status pill — positive indicator when GPS is active before location-sensitive actions */}
      {nextCfg && ['en_route', 'client_on_board', 'dropped_off'].includes(mission.next_status) && canStartMission && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg"
          data-testid="gps-status-indicator">
          <LocateFixed className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-400 text-xs font-medium">GPS actif — position disponible</span>
        </div>
      )}

      {/* Main Action Button */}
      {nextCfg && (
        <>
          <Button
            onClick={handleUpdateStatus}
            disabled={statusAction.loading || (mission.next_status === 'en_route' && !canStartMission)}
            className={`w-full ${nextCfg.color} text-white min-h-[52px] text-sm font-semibold touch-manipulation ${mission.next_status === 'en_route' && !canStartMission ? 'opacity-40 cursor-not-allowed' : ''}`}
            data-testid={`btn-${mission.next_status}`}
          >
            {statusAction.loading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <nextCfg.icon className="w-5 h-5 mr-2" />
            )}
            {nextCfg.label}
          </Button>
          {gpsError && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-400 text-xs">{gpsError}</p>
            </div>
          )}
        </>
      )}

      {/* Stop button - T7: Show "Repartir" if stopped, otherwise "Marquer un arrêt" */}
      {mission.is_stopped ? (
        <Button
          onClick={handleResumeAfterStop}
          disabled={resumeAction.loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white min-h-[48px] touch-manipulation"
          data-testid="btn-resume-after-stop"
        >
          {resumeAction.loading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <PlayCircle className="w-5 h-5 mr-2" />
          )}
          Repartir
        </Button>
      ) : mission.status === 'client_on_board' ? (
        <Button
          onClick={() => setShowStopModal(true)}
          variant="outline"
          className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 min-h-[48px] touch-manipulation"
          data-testid="btn-report-stop"
        >
          <PauseCircle className="w-5 h-5 mr-2" /> Marquer un arrêt
        </Button>
      ) : null}

      {/* Trip Section */}
      <Section title="Trajet" icon={MapPin} testId="section-trajet">
        <div className="space-y-3">
          <div className="flex items-start gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[#E5E5E5]/35 text-[10px]">Départ</p>
              <p className="text-[#E5E5E5] text-sm">{mission.pickup?.address}</p>
              {mission.pickup?.lat && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${mission.pickup.lat},${mission.pickup.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#C9A24D] text-xs mt-1 hover:underline min-h-[44px] touch-manipulation"
                >
                  <Navigation className="w-3 h-3" /> Ouvrir dans Maps
                </a>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[#E5E5E5]/35 text-[10px]">Arrivée</p>
              <p className="text-[#E5E5E5] text-sm">{mission.dropoff?.address}</p>
              {mission.dropoff?.lat && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${mission.dropoff.lat},${mission.dropoff.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#C9A24D] text-xs mt-1 hover:underline min-h-[44px] touch-manipulation"
                >
                  <Navigation className="w-3 h-3" /> Ouvrir dans Maps
                </a>
              )}
            </div>
          </div>
          <div className="pt-3 border-t border-white/6 flex items-center gap-2 text-[#E5E5E5] text-sm">
            <Calendar className="w-4 h-4 text-[#C9A24D]" />
            {fmtFull(mission.pickup_datetime)}
          </div>
          {mission.is_round_trip && (
            <div className="flex items-center gap-2 text-[#C9A24D] text-xs">
              <Clock className="w-3.5 h-3.5" />
              Aller-retour · Retour : {fmtFull(mission.return_datetime)}
            </div>
          )}
        </div>
      </Section>

      {/* Service Section */}
      <Section title="Service" icon={Car} testId="section-service">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[#E5E5E5]/35 text-[10px]">Type</p>
            <p className="text-white">{mission.service_type === 'transfer' ? 'Transfert' : mission.service_type === 'excursion' ? 'Excursion' : 'Mise à disposition'}</p>
          </div>
          <div>
            <p className="text-[#E5E5E5]/35 text-[10px]">Catégorie demandée</p>
            <p className="text-white">{vehicleNames[mission.vehicle_category] || mission.vehicle_category}</p>
          </div>
          <div>
            <p className="text-[#E5E5E5]/35 text-[10px]">Passagers</p>
            <p className="text-white">{mission.passengers}</p>
          </div>
          <div>
            <p className="text-[#E5E5E5]/35 text-[10px]">Bagages</p>
            <p className="text-white">{mission.luggage}</p>
          </div>
          {/* Driver fee with HT/TTC display */}
          {mission.driver_fee && (() => {
            const fee = formatFee(mission.driver_fee);
            return fee ? (
              <div className="col-span-2 pt-2 border-t border-white/6" data-testid="mission-fee-banner">
                <p className="text-[#E5E5E5]/35 text-[10px] mb-1">Votre tarif</p>
                <div className="flex items-end gap-1.5">
                  <p className="text-[#C9A24D] text-xl font-bold" data-testid="mission-driver-fee">
                    {parseFloat(fee.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </p>
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#C9A24D]/15 text-[#C9A24D]/80 mb-1">{fee.label}</span>
                </div>
              </div>
            ) : null;
          })()}
          {mission.assigned_vehicle && (
            <div className="col-span-2 pt-2 border-t border-white/6">
              <p className="text-[#E5E5E5]/35 text-[10px] mb-1.5">Véhicule assigné</p>
              <div className="flex items-center gap-2.5 p-2.5 bg-emerald-500/5 border border-emerald-500/15 rounded-lg">
                <Car className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium">{mission.assigned_vehicle.vehicle_name}</p>
                  <p className="text-white/40 text-[10px]">{mission.assigned_vehicle.license_plate}{mission.assigned_vehicle.vehicle_category ? ` \u00b7 ${vehicleNames[mission.assigned_vehicle.vehicle_category] || mission.assigned_vehicle.vehicle_category}` : ''}</p>
                </div>
                {mission.fleet_name && (
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">{mission.fleet_name}</span>
                )}
              </div>
            </div>
          )}
          {mission.options?.length > 0 && (
            <div className="col-span-2 pt-2 border-t border-white/6">
              <p className="text-[#E5E5E5]/35 text-[10px] mb-1.5">Options</p>
              <div className="flex flex-wrap gap-1.5">
                {mission.options.map((o) => (
                  <span key={o} className="px-2 py-0.5 bg-[#C9A24D]/10 text-[#C9A24D] text-[10px] rounded">
                    {optionNames[o] || o}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Special Requests */}
      {mission.special_requests && (
        <Card className="bg-yellow-500/8 border-yellow-500/20">
          <CardContent className="p-4 flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-yellow-400 text-xs font-semibold mb-1">Instructions spéciales</p>
              <p className="text-[#E5E5E5] text-sm">{mission.special_requests}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Section */}
      <Section title="Client" icon={User} testId="section-client">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#E5E5E5]/30" />
            <span className="text-white text-sm">{mission.client?.name}</span>
          </div>
          {showContact ? (
            <>
              {mission.client?.phone && (
                <a
                  href={`tel:${mission.client.phone}`}
                  className="flex items-center gap-2 text-[#C9A24D] text-sm hover:underline min-h-[44px] touch-manipulation"
                >
                  <Phone className="w-4 h-4" /> {mission.client.phone}
                </a>
              )}
              {mission.client?.email && (
                <div className="flex items-center gap-2 text-[#E5E5E5]/50 text-xs">
                  <Mail className="w-4 h-4" /> {mission.client.email}
                </div>
              )}
            </>
          ) : (
            <p className="text-[#E5E5E5]/35 text-xs">
              Coordonnées visibles une fois la mission démarrée
            </p>
          )}
        </div>
      </Section>

      {/* History Section */}
      {mission.status_history?.length > 0 && (
        <Section title="Historique" icon={History} defaultOpen={false} testId="section-historique">
          <div className="space-y-2.5">
            {mission.status_history
              .slice()
              .reverse()
              .map((entry, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#C9A24D] mt-1.5 shrink-0" />
                  <div className="flex items-center gap-2 flex-wrap">
                    {badge(entry.status)}
                    <span className="text-[#E5E5E5]/30 text-[10px]">{fmtShort(entry.changed_at)}</span>
                  </div>
                </div>
              ))}
          </div>
        </Section>
      )}

      {/* Incident */}
      {isInProgress && (
        <Button
          variant="outline"
          onClick={() => setShowIncident(true)}
          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 min-h-[44px] touch-manipulation"
          data-testid="incident-btn"
        >
          <AlertTriangle className="w-4 h-4 mr-2" /> Signaler un incident
        </Button>
      )}

      {/* Back */}
      <Link to={backTo} className="block">
        <Button
          variant="outline"
          className="w-full border-white/15 text-[#E5E5E5] min-h-[44px] touch-manipulation"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux missions
        </Button>
      </Link>

      {/* Stop Modal */}
      {showStopModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-4">
          <Card className="bg-[#1a1a1a] border-white/10 w-full max-w-md">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <PauseCircle className="w-5 h-5 text-amber-400" /> Marquer un arrêt
              </h3>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1.5 block">Raison de l'arrêt</label>
                <select
                  value={stopReason}
                  onChange={(e) => setStopReason(e.target.value)}
                  className="w-full h-11 px-3 bg-[#0B0B0B] border border-white/15 rounded-md text-white text-sm"
                  data-testid="stop-reason-select"
                >
                  <option value="">Sélectionner...</option>
                  <option value="Pause conducteur">Pause conducteur</option>
                  <option value="Demande client">Demande client</option>
                  <option value="Carburant">Carburant</option>
                  <option value="Problème technique">Problème technique</option>
                  <option value="Embouteillage / Trafic">Embouteillage / Trafic</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowStopModal(false)}
                  className="flex-1 border-white/15 text-[#E5E5E5] min-h-[44px] touch-manipulation"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleReportStop}
                  disabled={!stopReason || stopAction.loading}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white min-h-[44px] touch-manipulation"
                  data-testid="confirm-stop-btn"
                >
                  {stopAction.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer l\'arrêt'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Incident Modal */}
      {showIncident && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-4">
          <Card className="bg-[#1a1a1a] border-white/10 w-full max-w-md">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" /> Signaler un incident
              </h3>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1.5 block">Type d'incident</label>
                <select
                  value={incidentType}
                  onChange={(e) => setIncidentType(e.target.value)}
                  className="w-full h-11 px-3 bg-[#0B0B0B] border border-white/15 rounded-md text-white text-sm"
                >
                  <option value="">Sélectionner...</option>
                  {incidentTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1.5 block">Sévérité</label>
                <select
                  value={incidentSev}
                  onChange={(e) => setIncidentSev(e.target.value)}
                  className="w-full h-11 px-3 bg-[#0B0B0B] border border-white/15 rounded-md text-white text-sm"
                >
                  <option value="low">Faible</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                </select>
              </div>
              <div>
                <label className="text-[#E5E5E5]/50 text-xs mb-1.5 block">Description</label>
                <textarea
                  value={incidentDesc}
                  onChange={(e) => setIncidentDesc(e.target.value)}
                  placeholder="Décrivez l'incident..."
                  className="w-full p-3 bg-[#0B0B0B] border border-white/15 rounded-md text-white text-sm h-24 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowIncident(false)}
                  className="flex-1 border-white/15 text-[#E5E5E5] min-h-[44px] touch-manipulation"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleReportIncident}
                  disabled={!incidentType || !incidentDesc || incidentAction.loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white min-h-[44px] touch-manipulation"
                >
                  {incidentAction.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Signaler'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChauffeurMissionDetailPage;
